import { v4 as uuidv4 } from 'uuid';
import api from '@/services/api';
import { ApiResponse, PosSession } from '@/services/types';
import { API_ENDPOINTS, STORAGE_KEYS, POS_SESSION_REFRESH_MS } from '@/utils/constants';
import { registerPush, unregisterPush } from '@/features/notifications/push';

/**
 * POS session: the backend only delivers push notifications to a shop with an open session.
 * Opening a session ends the user's previous ones server-side, so a shop switch needs no close.
 * Every call is fire-and-forget: failure degrades to "no push", never blocks the UI.
 */
interface Stored { shopId: number; sessionToken: string; openedAt: number }

const getBrowserId = () => {
    let id = localStorage.getItem(STORAGE_KEYS.BROWSER_ID);
    if (!id) {
        id = uuidv4();
        localStorage.setItem(STORAGE_KEYS.BROWSER_ID, id);
    }
    return id;
};

const load = (): Stored | null => {
    try {
        const raw = localStorage.getItem(STORAGE_KEYS.POS_SESSION);
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
};

export async function openPosSession(shopId: number) {
    try {
        const res = await api.post<ApiResponse<PosSession>>(API_ENDPOINTS.SESSION_OPEN(shopId), null, {
            params: { browserId: getBrowserId() },
        });
        const stored: Stored = { shopId, sessionToken: res.data.data.sessionToken, openedAt: Date.now() };
        localStorage.setItem(STORAGE_KEYS.POS_SESSION, JSON.stringify(stored));
        registerPush(shopId);
    } catch (e) {
        console.warn('[session] open failed', e);
    }
}

/** Open if missing, for another shop, or older than the refresh window. Call on shop select and app resume. */
export function ensurePosSession(shopId: number) {
    const s = load();
    if (!s || s.shopId !== shopId || Date.now() - s.openedAt >= POS_SESSION_REFRESH_MS) {
        return openPosSession(shopId);
    }
    return Promise.resolve();
}

/** Must run before cookies are cleared (i.e. before POST /auth/logout). */
export async function closePosSession() {
    const s = load();
    if (!s) return;
    try {
        await unregisterPush(s.shopId);
        await api.post(API_ENDPOINTS.SESSION_CLOSE(s.shopId), null, { params: { sessionToken: s.sessionToken } });
    } catch (e) {
        console.warn('[session] close failed', e);
    } finally {
        localStorage.removeItem(STORAGE_KEYS.POS_SESSION);
    }
}
